// The 3-5-8 table (.ai/358.md): the game of 358/rules.ts, the 2 computer players' timing (the choice of the game, each
// card of the exchange, the musik, the cards laid away, every card played), the player's own steps, the clock and the
// score window after each deal. The computer waits while the Pause window is open or the tab is hidden. No Undo (as in
// Kierki).
import { useSyncExternalStore } from 'react';
import {
  botDeclare, botDiscard, botGive, botGiveBack, botPlay, cardKey, collectTrick, declare, discard, giveBack, giveCard, playCard, sameCard,
  startGame, startHand, stopGiving, takeMusik, type Contract, type HCard, type State358, type SwapDone,
} from './rules';
import { t } from '../i18n';
import { SLOW } from '../hearts/store';
import { drawNames, fixedNames, nameList, type SeatNames } from '../hearts/names';

/** A computer player's card, and the full trick before it is taken (as Kierki). */
export const BOT_MS = 700;
export const TRICK_MS = 1200;
export const COLLECT_MS = 420;
/** A computer declarer thinks this long over its game. */
export const DECLARE_MS = 1300;
/** Each card of the exchange (a computer player's), and its flights there and back. */
export const SWAP_MS = 1300;
export const SWAP_FLY_MS = 480;
/** The musik lies face up this long before the declarer takes it (everyone sees it), then flies to him. */
export const MUSIK_SHOW_MS = 1900;
export const MUSIK_FLY_MS = 520;
/** A computer declarer lays its 4 cards away after this. */
export const DISCARD_MS = 1200;
/** The deal (Table358.tsx useDealAnimation): 52 cards round the table and to the musik. */
export const DEAL_STEP_MS = 55;
export const DEAL_FLIGHT_MS = 360;
export const DEAL_MS = 51 * DEAL_STEP_MS + DEAL_FLIGHT_MS + 150;

export interface TableState {
  readonly game: State358 | null;
  /** The player's cards chosen to lay away (up to 4). */
  readonly selected: readonly HCard[];
  /** Seconds of play in this game (the status bar's clock). */
  readonly seconds: number;
  /** Counts the deals shown (the view's deal animation starts on a change). */
  readonly deal: number;
  /** The full trick slides to its taker now. */
  readonly collecting: boolean;
  /** Counts the exchanges shown (the view's flights start on a change); the last one. */
  readonly swap: number;
  readonly lastSwap: SwapDone | null;
  /** The cards the player played in this game (a win's `moves`). */
  readonly playedByPlayer: number;
  /** A position put in by the e2e hook (load): its end is never saved as a win. */
  readonly demo: boolean;
  readonly names: SeatNames | null;
  /** When the game began and ended (Date.now(); the score window's New game: hearts/nextGame.ts). */
  readonly startedAt: number;
  readonly endedAt: number | null;
  /** Online (online/three-five-eight.ts): when my move runs out (Date.now() ms), then the server's bot plays it; unset
   *  offline. */
  readonly deadlineAt?: number | null;
}

export interface Store358 {
  getState(): TableState;
  subscribe(fn: () => void): () => void;
  /** A new game of `length` deals: `pattern` (52 numbers, engine/shuffle.ts) gives its first deal (Retry, a report). */
  newGame(pattern: readonly number[] | null, length: number): void;
  nextHand(): void;
  /** The player names the game. */
  choose(c: Contract): void;
  /** A tap on a card of the hand, read by the phase: give it in the exchange, give it back for a trump, choose it to lay
   *  away, play it. */
  tap(card: HCard): void;
  /** The player gives no more cards in the exchange. */
  stopGiving(): void;
  /** The player lays the 4 chosen cards away. */
  discard(): void;
  stop(): void;
  /** e2e only: show a position as if the game had reached it. */
  load(g: State358): void;
  /** e2e only: the game began `ms` earlier. */
  backdate(ms: number): void;
}

export interface Hooks358 {
  rng: () => number;
  paused: () => boolean;
  onStep?: (what: string) => void;
  /** A deal or the game ended (the score window opens). */
  onHandEnd?: (s: State358) => void;
}

export function create358Store(hooks: Hooks358): Store358 {
  let state: TableState = { game: null, selected: [], seconds: 0, deal: 0, collecting: false, swap: 0, lastSwap: null, playedByPlayer: 0, demo: false, names: null, startedAt: 0, endedAt: null };
  const listeners = new Set<() => void>();
  let timer: ReturnType<typeof setTimeout> | null = null;
  let clock: ReturnType<typeof setInterval> | null = null;
  let dealing = false;
  const set = (patch: Partial<TableState>) => {
    state = { ...state, ...patch };
    for (const fn of listeners) fn();
  };
  const step = (what: string) => hooks.onStep?.(what);
  const clearTimer = () => {
    if (timer !== null) clearTimeout(timer);
    timer = null;
  };
  /** Runs `fn` after `ms`, later again while the game is paused. */
  const after = (ms: number, fn: () => void) => {
    clearTimer();
    timer = setTimeout(function run() {
      if (hooks.paused()) {
        timer = setTimeout(run, 300);
        return;
      }
      timer = null;
      fn();
    }, ms * SLOW);
  };
  const startClock = () => {
    if (clock !== null) return;
    clock = setInterval(() => {
      const g = state.game;
      if (!g || g.phase === 'over' || g.phase === 'hand-end' || hooks.paused()) return;
      set({ seconds: state.seconds + 1 });
    }, 1000);
  };
  const stopClock = () => {
    if (clock !== null) clearInterval(clock);
    clock = null;
  };

  /** An exchange was done: the view flies the two cards, the next step waits for them. */
  const swapped = (g: State358) => {
    const done = g.swapLog[g.swapLog.length - 1]!;
    step(`exchange ${done.giver}->${done.taker}: ${cardKey(done.gave)} for ${cardKey(done.got)}`);
    set({ game: g, swap: state.swap + 1, lastSwap: done });
    after(SWAP_FLY_MS * 2 + 150, () => advance(state.game ?? g));
  };

  /** After every change: the computer's next step, the musik, the trick taken, or the deal's end. */
  const advance = (g: State358) => {
    set({ game: g });
    if (dealing) return;
    if (g.phase === 'declare') {
      if (g.declarer === 0) return;
      after(DECLARE_MS, () => {
        const cur = state.game!;
        if (cur.phase !== 'declare') return;
        const c = botDeclare(cur, cur.declarer);
        step(`seat ${cur.declarer} declares ${c}`);
        advance(declare(cur, cur.declarer, c)!);
      });
      return;
    }
    if (g.phase === 'exchange') {
      const p = g.pendingTrump;
      if (p) {
        if (p.taker === 0) return;
        after(SWAP_MS * 0.6, () => {
          const cur = state.game!;
          if (!cur.pendingTrump) return;
          swapped(giveBack(cur, cur.pendingTrump.taker, botGiveBack(cur, cur.pendingTrump.taker))!);
        });
        return;
      }
      const giver = g.swaps[0]!.giver;
      if (giver === 0) return;
      after(SWAP_MS, () => {
        const cur = state.game!;
        if (cur.phase !== 'exchange' || cur.pendingTrump || cur.swaps[0]?.giver !== giver) return;
        const card = botGive(cur, giver);
        if (!card) return advance(stopGiving(cur, giver)!);
        const next = giveCard(cur, giver, card)!;
        if (next.pendingTrump) advance(next);
        else swapped(next);
      });
      return;
    }
    if (g.phase === 'musik') {
      if (g.musik.length) {
        // everyone sees the musik, then it goes to the declarer
        after(MUSIK_SHOW_MS, () => {
          const cur = state.game!;
          if (cur.phase !== 'musik' || !cur.musik.length) return;
          step(`musik ${cur.musik.map(cardKey).join(' ')} to seat ${cur.declarer}`);
          const next = takeMusik(cur);
          set({ game: next });
          after(MUSIK_FLY_MS + 100, () => advance(state.game ?? next));
        });
        return;
      }
      if (g.declarer === 0) return;
      after(DISCARD_MS, () => {
        const cur = state.game!;
        if (cur.phase !== 'musik' || cur.musik.length) return;
        advance(discard(cur, cur.declarer, botDiscard(cur, cur.declarer))!);
      });
      return;
    }
    if (g.phase === 'play' && g.turn !== 0) {
      after(BOT_MS, () => {
        const cur = state.game!;
        if (cur.phase !== 'play' || cur.turn === 0) return;
        advance(playCard(cur, cur.turn, botPlay(cur, cur.turn))!);
      });
      return;
    }
    if (g.phase === 'trick-done') {
      after(TRICK_MS - COLLECT_MS, () => {
        set({ collecting: true });
        after(COLLECT_MS, () => {
          const cur = state.game!;
          step(`trick ${cur.tricks.reduce((a, b) => a + b, 0) + 1} to seat ${cur.winner} (${cur.trick.map((p) => cardKey(p.card)).join(' ')})`);
          set({ collecting: false });
          advance(collectTrick(cur));
        });
      });
      return;
    }
    if (g.phase === 'hand-end' || g.phase === 'over') {
      if (g.phase === 'over') set({ endedAt: Date.now() });
      const r = g.history[g.history.length - 1]!;
      step(`deal ${g.handNo + 1} (${r.contract} by ${r.declarer}): tricks ${r.tricks.join(' ')}, points ${r.points.join(' ')} (totals ${g.scores.join(' ')})`);
      hooks.onHandEnd?.(g);
    }
  };

  /** A new deal: shown at once (the view throws the cards out), played on once the deal is over. */
  const dealt = (g: State358) => {
    dealing = true;
    set({ game: g, deal: state.deal + 1, selected: [], lastSwap: null });
    after(DEAL_MS, () => {
      dealing = false;
      advance(state.game ?? g);
    });
  };

  const mine = (phase: State358['phase']) => {
    const g = state.game;
    return g && g.phase === phase && !dealing ? g : null;
  };

  return {
    getState: () => state,
    subscribe(fn) {
      listeners.add(fn);
      return () => listeners.delete(fn);
    },
    newGame(pattern, length) {
      clearTimer();
      set({ selected: [], seconds: 0, collecting: false, playedByPlayer: 0, demo: false, startedAt: Date.now(), endedAt: null, names: drawNames(hooks.rng, nameList(t('hearts.names')), t('hearts.you')) ?? fixedNames(t) });
      const g = startGame(length, pattern, hooks.rng);
      step(`3-5-8 game of ${length} deals, deal 1`);
      startClock();
      dealt(g);
    },
    nextHand() {
      const g = state.game;
      if (!g || g.phase !== 'hand-end') return;
      clearTimer();
      const n = startHand(g, null, hooks.rng);
      step(`deal ${n.handNo + 1}, seat ${n.declarer} declares`);
      dealt(n);
    },
    choose(c) {
      const g = mine('declare');
      if (!g || g.declarer !== 0) return;
      const next = declare(g, 0, c);
      if (!next) return;
      step(`declared ${c}`);
      advance(next);
    },
    tap(card) {
      const g = state.game;
      if (!g || dealing) return;
      if (g.phase === 'exchange' && g.pendingTrump?.taker === 0) {
        const next = giveBack(g, 0, card);
        if (next) swapped(next);
        return;
      }
      if (g.phase === 'exchange' && !g.pendingTrump && g.swaps[0]?.giver === 0) {
        const next = giveCard(g, 0, card);
        if (!next) return;
        if (next.pendingTrump) advance(next);
        else swapped(next);
        return;
      }
      if (g.phase === 'musik' && !g.musik.length && g.declarer === 0) {
        const has = state.selected.some((c) => sameCard(c, card));
        if (has) set({ selected: state.selected.filter((c) => !sameCard(c, card)) });
        else if (state.selected.length < 4) set({ selected: [...state.selected, card] });
        return;
      }
      if (g.phase === 'play' && g.turn === 0) {
        const next = playCard(g, 0, card);
        if (!next) {
          step(`card refused ${cardKey(card)}`);
          return;
        }
        set({ playedByPlayer: state.playedByPlayer + 1 });
        advance(next);
      }
    },
    stopGiving() {
      const g = mine('exchange');
      const next = g && stopGiving(g, 0);
      if (!next) return;
      step('stopped giving');
      advance(next);
    },
    discard() {
      const g = mine('musik');
      if (!g || state.selected.length !== 4) return;
      const next = discard(g, 0, state.selected);
      if (!next) return;
      step(`laid away ${state.selected.map(cardKey).join(' ')}`);
      set({ selected: [] });
      advance(next);
    },
    stop() {
      clearTimer();
      stopClock();
    },
    load(g) {
      clearTimer();
      dealing = false;
      set({ selected: [], collecting: false, demo: true, lastSwap: null });
      advance(g);
    },
    backdate(ms) {
      set({ startedAt: state.startedAt - ms });
    },
  };
}

let current: Store358 | null = null;
export const set358Store = (s: Store358) => {
  current = s;
};
export const store358 = (): Store358 => {
  if (!current) throw new Error('store358: not created');
  return current;
};
export function use358(): TableState {
  const s = store358();
  return useSyncExternalStore(s.subscribe, s.getState, s.getState);
}

/** The table as text for a report: hands (the computer's too: a report is for us), the game, the trick, the totals. */
export function boardText358(st: TableState): Record<string, unknown> | null {
  const g = st.game;
  if (!g) return null;
  const k = (cards: readonly HCard[]) => cards.map(cardKey).join(' ');
  return {
    deal: `${g.handNo + 1}/${g.length}`, phase: g.phase, dealer: g.dealer, declarer: g.declarer, contract: g.contract, turn: g.turn,
    names: (st.names ?? fixedNames(t)).name.slice(1, 3).join(', '), hands: g.hands.map(k), musik: k(g.musikCards), discards: k(g.discards),
    swaps: g.swaps, trick: g.trick.map((p) => `${p.seat}:${cardKey(p.card)}`).join(' '), tricks: g.tricks, totals: g.scores,
    history: g.history.map((r) => `${r.contract}@${r.declarer} ${r.points.join('/')}`), selected: k(st.selected), pattern: g.pattern.join(','),
  };
}
